<script setup lang="ts">
  import Popup from '@/components/Popup.vue'
  import Table from '@/components/table/Table.vue'
  import TableColumnSheet from '@/components/table/TableColumnSheet.vue'
  import TableFilter from '@/components/table/TableFilter.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useShipmentStore } from '@/store/shipment.store'
  import type { Shipment } from '@/types/nest/shipment'
  import { axiosClient } from '@/utils/axios-client'
  import { createBaseColumns } from '@/utils/table'
  import type { Nullish } from '@logistiek-zonder-papier/lzp-library'
  import {
    DateRangePicker,
    UiButton,
    useToastStore,
    type Column,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { isAfter, isBefore, startOfToday, subDays } from 'date-fns'
  import { useTranslation } from 'i18next-vue'
  import { computed, onMounted, ref } from 'vue'

  const shipmentStore = useShipmentStore()
  const { i18next } = useTranslation()

  const errorMessage = ref<string | null>()
  const fromDate = ref<Date | Nullish>(subDays(startOfToday(), 7))
  const toDate = ref<Date | Nullish>(startOfToday())
  const toastStore = useToastStore()

  const defaultColumns = [
    'startTime',
    'endTime',
    'fromAddress.street',
    'fromAddress.city',
    'toAddress.street',
    'toAddress.city',
    'truck.licensePlate',
    'actions',
  ]

  const visibleColumns = ref<Column[]>([])
  const showColumnSheet = ref<boolean>(false)

  const shipments = computed<Shipment[]>(() =>
    shipmentStore.shipments
      .filter(shipment => {
        if (fromDate.value == null && toDate.value == null) return true
        if (shipment.startTime == null) return false
        if (isInDateRange(new Date(shipment.startTime))) return true
        if (shipment.endTime == null) return false

        return isInDateRange(new Date(shipment.endTime))
      })
      .sort(
        (lhs, rhs) =>
          new Date(rhs.startTime ?? '').getTime() - new Date(lhs.startTime ?? '').getTime()
      )
  )

  const dateFilterLabel = computed(() => {
    if (fromDate.value && toDate.value) {
      return `${formatDate(fromDate.value)} – ${formatDate(toDate.value)}`
    }
    if (fromDate.value) return `Vanaf ${formatDate(fromDate.value)}`
    if (toDate.value) return `Tot ${formatDate(toDate.value)}`
    return 'Alle datums'
  })

  function formatDate(date: Date) {
    return date.toLocaleDateString(i18next.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  function isInDateRange(date: Date) {
    if (fromDate.value && !isAfter(date, fromDate.value)) return false
    if (toDate.value && !isBefore(date, toDate.value)) return false
    return true
  }

  function clearDates() {
    fromDate.value = null
    toDate.value = null
  }

  onMounted(async () => {
    await fetchShipments()
  })

  async function fetchShipments() {
    try {
      await shipmentStore.fetchShipments()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        errorMessage.value = error.response.data.message
      } else {
        throw error
      }
    }
  }

  async function excelExport() {
    if (shipments.value.length === 0) {
      toastStore.error('Geen zendingen gevonden in deze datum range')
      return
    }
    const response = await axiosClient.request({
      url: '/excel/bgb', // TODO support other export types
      responseType: 'blob',
      params: {
        startTime: fromDate.value?.toISOString(),
        endTime: toDate.value?.toISOString(),
      },
    })

    const disposition = response.headers['content-disposition']
    let filename = 'rapport.xlsx'
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/)
      if (match) filename = match[1]
    }

    const blob = new Blob([response.data], {
      type: String(response.headers['content-type']),
    })
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    link.click()
    URL.revokeObjectURL(blobUrl)
  }

  async function bulkPdf() {
    if (shipments.value.length === 0) {
      toastStore.error('Geen zendingen gevonden in deze datum range')
      return
    }
    const response = await axiosClient.request({
      url: `/shipments/pdf-bulk`,
      method: 'POST',
      responseType: 'blob',
      data: {
        shipmentIds: shipments.value.map(shipment => shipment.id),
      },
    })

    window.open(URL.createObjectURL(response.data), '_blank')
  }

  async function download(downloadFile: () => Promise<void>, close: () => void) {
    close()
    await downloadFile()
  }

  async function openPdf(row: Shipment) {
    const response = await axiosClient.request({
      url: `/shipments/${row.id}/pdf`,
      method: 'POST',
      responseType: 'blob',
    })

    window.open(URL.createObjectURL(response.data), '_blank')
  }

  const columns = computed<Column[]>(() => {
    const rows = shipmentStore.shipments
    if (rows.length === 0) return []

    const baseColumns = createBaseColumns(rows, [
      /createdAt/,
      /updatedAt/,
      /deletedAt/,
      /id/,
      /Id$/,
      /customer.bi/,
      /customer.auth/,
      /customer.iban/,
      /customer.taxNumber/,
    ])
    return [
      ...baseColumns.map(column => ({ ...column, searchable: true })),
      { key: 'actions', label: 'Acties', sticky: 'right' },
    ]
  })
</script>

<template>
  <TopBar>
    <UiButton v-if="shipments.length > 0" icon="more" @click="showColumnSheet = true" />

    <Popup align="end">
      <template #trigger="{ toggle }">
        <UiButton icon="download" @click="toggle" />
      </template>

      <template #default="{ close }">
        <div class="download-options">
          <UiButton label="Bulk PDF" @click="download(bulkPdf, close)" />
          <UiButton label="Excel export" @click="download(excelExport, close)" />
        </div>
      </template>
    </Popup>
  </TopBar>

  <TableColumnSheet
    v-model:show="showColumnSheet"
    v-model:visible-columns="visibleColumns"
    :columns="columns"
    :default-columns="defaultColumns"
  />

  <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

  <div class="layout" v-else>
    <div class="filters">
      <TableFilter
        :label="dateFilterLabel"
        :clearable="fromDate != null || toDate != null"
        @clear="clearDates"
      >
        <DateRangePicker
          :fromOptions="{ label: 'Van' }"
          :toOptions="{ label: 'Tot' }"
          v-model:from="fromDate"
          v-model:to="toDate"
        />
      </TableFilter>
    </div>

    <Table :rows="shipments" :columns="visibleColumns" row-key="id">
      <template #cell-actions="{ row }">
        <UiButton variant="link" label="PDF" @click="openPdf(row)" />
      </template>
      <template #cell-truck="{ row }">{{ row.truck?.licensePlate ?? 'N/A' }}</template>
      <template #cell-startTime="{ row }">{{
        row.startTime ? new Date(row.startTime).toLocaleString() : '-'
      }}</template>
      <template #cell-endTime="{ row }">{{
        row.endTime ? new Date(row.endTime).toLocaleString() : '-'
      }}</template>
    </Table>
  </div>
</template>

<style lang="css" scoped>
  .error-message {
    color: var(--colours-support-negative);
  }

  .download-options {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    white-space: nowrap;
  }

  .filters {
    display: flex;
    gap: var(--spacing-m);
  }

  .filters .form-field-container {
    margin-bottom: 0;
  }

  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-s);
    min-height: calc(100dvh - var(--top-bar-height));
  }
</style>
